import { Delete, WarningAmberOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { PARTNER_TRANSFERS_LIST } from 'adminConstants/routes';
import DataLossWarningModal from 'components/DataLossWarningModal';
import AddInvestorDrawer from 'components/new/AddInvestorDrawer';
import Page from 'components/new/Page';
import { AccountType } from 'further-types/investor';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { SortOrder } from 'further-types/utils';
import BasicSelect from 'further-ui/components/BasicSelect';
import DatePicker from 'further-ui/components/DatePicker';
import FormField from 'further-ui/components/FormField';
import IconButton from 'further-ui/components/IconButton';
import { Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import { useDisclosure } from 'further-ui/hooks';
import {
  CalendarDay,
  dateToLabel,
  numberToCurrencyString,
} from 'further-ui/utils';
import { useDrawerUrlState } from 'hooks/ui/useDrawerUrlState';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import { round, sumBy } from 'lodash';
import React, { useState } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { PartnerTransfersFormValues } from '../hooks/usePartnerTransfersForm';
import PartnerTransferActionBar from '../PartnerTransferActionBar';

const WarningIcon = styled(WarningAmberOutlined)(({ theme }) => ({
  color: theme.palette.warning.main,
  marginLeft: theme.spacing(1),
  cursor: 'pointer',
}));

type Props = {
  onNext: (partnerTransferId?: string) => void;
  isSubmitting?: boolean;
  baseCurrency: CurrencyCode;
  vehicleId: string;
  id?: string;
  stepper: React.ReactNode;
};

const AddTransfer: React.FC<Props> = ({
  onNext,
  isSubmitting = false,
  baseCurrency,
  vehicleId,
  id,
  stepper,
}) => {
  const formMethods = useFormContext<PartnerTransfersFormValues>();
  const selectedTrasferrerCommitmentId = formMethods.watch(
    'trasferrorCommitmentId',
  );

  const notification = useNotification();
  const utils = api.useUtils();

  const { fields, append, remove, replace } = useFieldArray({
    control: formMethods.control,
    name: 'transferees',
  });

  const transferrerChangeDisclosure = useDisclosure();
  const { activeDrawer, openDrawer, closeDrawer } = useDrawerUrlState({
    addInvestor: null,
  });
  const [pendingTransferrerCommitmentId, setPendingTransferrerCommitmentId] =
    useState<string>('');
  const [previousTransferrerCommitmentId, setPreviousTransferrerCommitmentId] =
    useState<string>('');

  const [commitments] = api.commitments.list.useSuspenseQuery({
    vehicleId,
    onlyEqualised: true,
    order: SortOrder.Desc,
    page: 1,
    rowsPerPage: 1000,
    orderBy: 'commitmentDate',
  });

  const [investors] = api.lpInvestor.getAll.useSuspenseQuery();

  const createTransferMutation = api.partnerTransfers.create.useMutation({
    onSuccess: (data) => {
      notification.success('Partner transfer created successfully.');
      utils.partnerTransfers.getById.invalidate();
      //TODO: Invalidate allocations and transaction queries

      if (data?.partnerTransfer) {
        formMethods.setValue('partnerTransferId', data.partnerTransfer.id);
        formMethods.setValue(
          'currentSection',
          PartnerTransferSection.Investments,
        );
        onNext(data.partnerTransfer.id);
      }
    },
    onError: (err) => {
      notification.error(
        err?.data?.cause ?? err?.message ?? 'Something went wrong.',
      );
    },
  });

  const updateTransferMutation = api.partnerTransfers.update.useMutation({
    onSuccess: (_data) => {
      utils.partnerTransfers.getById.invalidate();
      //TODO: Invalidate allocations and transaction queries

      const partnerTransferId = formMethods.getValues('partnerTransferId');

      if (partnerTransferId) {
        onNext(partnerTransferId);
      }
    },
    onError: (err) => {
      notification.error(
        err?.data?.cause ?? err?.message ?? 'Something went wrong.',
      );
    },
  });

  const [dateBlockingErrors, setDateBlockingErrors] = useState<Array<string>>(
    [],
  );

  const { data: constraints } = api.partnerTransfers.getConstraints.useQuery(
    {
      vehicleId,
      partnerTransferId: id ?? formMethods.getValues('partnerTransferId'),
      commitmentId: selectedTrasferrerCommitmentId,
    },
    { enabled: !!selectedTrasferrerCommitmentId },
  );

  const allBlockingErrors = [
    ...(constraints?.blockingErrors ?? []),
    ...dateBlockingErrors,
  ];

  const blockingErrorsForDisable = allBlockingErrors.filter(
    (error) => !dateBlockingErrors.includes(error),
  );

  const validateTransferMutation = api.partnerTransfers.validate.useMutation({
    onSuccess: (data) => {
      setDateBlockingErrors(data.blockingErrors);
    },
  });

  const selectedCommitment = commitments.results.find(
    (commitment) => commitment.id === selectedTrasferrerCommitmentId,
  );

  const handleDateChange = (date: CalendarDay | null) => {
    if (date) {
      formMethods.setValue('transferDate', date);
      //TODO: Reset transaction values

      if (selectedTrasferrerCommitmentId) {
        const partnerTransferId =
          id ?? formMethods.getValues('partnerTransferId');
        validateTransferMutation.mutate({
          vehicleId,
          commitmentId: selectedTrasferrerCommitmentId,
          transferDate: date,
          partnerTransferId,
        });
      }
    } else {
      formMethods.resetField('transferDate');
      setDateBlockingErrors([]);
    }
  };

  const getAvailableInvestorsForTransferee = (index: number) => {
    const selectedInvestorId = formMethods.watch(
      `transferees.${index}.investorId`,
    );

    const alreadySelectedInvestorIds = fields
      .map((_, idx) => {
        if (idx === index) return null;
        return formMethods.watch(`transferees.${idx}.investorId`);
      })
      .filter(Boolean) as Array<string>;

    const availableInvestors = investors.filter((investor) => {
      return (
        investor.id === selectedInvestorId ||
        !alreadySelectedInvestorIds.includes(investor.id)
      );
    });

    const availableInvestorsWithName = availableInvestors.map((investor) => ({
      id: investor.id,
      investorLabel:
        investor.accountType === AccountType.Individual
          ? `${investor.firstName} ${investor.lastName}`
          : investor.registeredName,
    }));

    return availableInvestorsWithName;
  };

  const handleTransfereeInvestorChange = (index: number, value: string) => {
    const selectedInvestor = investors.find(
      (investor) => investor.id === value,
    );
    if (selectedInvestor) {
      const label =
        selectedInvestor.accountType === AccountType.Individual
          ? `${selectedInvestor.firstName} ${selectedInvestor.lastName}`
          : selectedInvestor.registeredName;
      formMethods.setValue(`transferees.${index}.investorLabel`, label);
      //TODO: Reset transaction values
    }
  };

  const addTransferee = () => {
    append({
      investorId: '',
      investorLabel: '',
      amountLcy: 0,
      percentage: 0,
    });
    //TODO: Reset transaction values
  };

  const removeTransferee = (index: number) => {
    remove(index);
    //TODO: Reset transaction values;
  };

  const hasTransfereesData = () => {
    return fields.some((_, index) => {
      const investorId = formMethods.watch(`transferees.${index}.investorId`);
      return investorId && investorId !== '';
    });
  };

  const resetTransferees = () => {
    replace([
      {
        investorId: '',
        investorLabel: '',
        amountLcy: 0,
        percentage: 0,
      },
    ]);
  };

  const handleTransferrerInvestorChange = (value: string) => {
    const hasData = hasTransfereesData();

    if (hasData && selectedTrasferrerCommitmentId) {
      setPendingTransferrerCommitmentId(value);
      setPreviousTransferrerCommitmentId(selectedTrasferrerCommitmentId);
      transferrerChangeDisclosure.onOpen();
    } else {
      formMethods.setValue('trasferrorCommitmentId', value);
      formMethods.resetField('transferDate');
      //TODO: Reset transaction values
      setDateBlockingErrors([]);
    }
  };

  const handleConfirmTransferrerChange = () => {
    formMethods.setValue(
      'trasferrorCommitmentId',
      pendingTransferrerCommitmentId,
    );
    formMethods.resetField('transferDate');
    //TODO: Reset transaction values
    setDateBlockingErrors([]);
    resetTransferees();
    transferrerChangeDisclosure.onClose();
  };

  const handleCancelTransferrerChange = () => {
    formMethods.setValue(
      'trasferrorCommitmentId',
      previousTransferrerCommitmentId,
    );
    setPendingTransferrerCommitmentId('');
    setPreviousTransferrerCommitmentId('');
    transferrerChangeDisclosure.onClose();
  };

  const handleNext = async (data: PartnerTransfersFormValues) => {
    const partnerTransferId = id ?? formMethods.getValues('partnerTransferId');
    const validationResult = await validateTransferMutation.mutateAsync({
      vehicleId,
      commitmentId: data.trasferrorCommitmentId,
      transferDate: data.transferDate,
      partnerTransferId,
    });

    if (!validationResult.isValid) {
      return;
    }

    const totalTransferredAmountLcy = sumBy(data.transferees, 'amountLcy');

    if (
      selectedCommitment &&
      totalTransferredAmountLcy > selectedCommitment.totalCommitmentLcy
    ) {
      notification.error(
        'The total to be transferred is in excess of the commitment amount - please adjust the proposed transfer amounts.  ',
      );
      return;
    }

    if (partnerTransferId) {
      await updateTransferMutation.mutateAsync({
        partnerTransferId,
        vehicleId,
        transferorCommitmentId: data.trasferrorCommitmentId,
        transferDate: data.transferDate,
        currentSection: PartnerTransferSection.PartnerTransfer,
        transferees: data.transferees.map((transferee) => ({
          investorId: transferee.investorId,
          amountLcy: transferee.amountLcy,
          percentage: transferee.percentage,
        })),
      });
    } else {
      await createTransferMutation.mutateAsync({
        vehicleId,
        transferorCommitmentId: data.trasferrorCommitmentId,
        transferDate: data.transferDate,
        transferees: data.transferees,
      });
    }
  };

  return (
    <Page
      goBackAction={PARTNER_TRANSFERS_LIST}
      spacing="lg"
      heading="Partner transfer"
      withHeadingDivider
      customActions={stepper}
      actionBar={
        <PartnerTransferActionBar
          onNext={formMethods.handleSubmit(handleNext)}
          isNextLoading={isSubmitting || validateTransferMutation.isPending}
          isNextDisabled={
            !formMethods.formState.isValid || allBlockingErrors.length > 0
          }
        />
      }
    >
      <FormField size="lg" label="Transfer from">
        <Controller
          control={formMethods.control}
          name="trasferrorCommitmentId"
          render={({ field, fieldState: { error } }) => (
            <BasicSelect
              fullWidth
              placeholder="Select a commitment"
              options={commitments.results.map((commitment) => ({
                label: `${commitment.investorName} - ${dateToLabel(
                  commitment.commitmentDate?.toUTCDate(),
                )}`,
                value: commitment.id,
              }))}
              value={field.value}
              onChange={handleTransferrerInvestorChange}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>

      <FormField size="lg" label="Transfer date">
        <Controller
          control={formMethods.control}
          name="transferDate"
          render={({ field, fieldState: { error } }) => (
            <Box display="flex" alignItems="center">
              <DatePicker
                value={field.value}
                onChange={handleDateChange}
                error={!!error}
                helperText={error?.message}
                fullWidth
                minDate={constraints?.minDate?.toUTCDate()}
                disabled={blockingErrorsForDisable.length > 0}
              />
              {allBlockingErrors.length > 0 && (
                <Tooltip
                  title={
                    <>
                      {allBlockingErrors.map((e, i) => (
                        <p key={i}>{e}</p>
                      ))}
                    </>
                  }
                  arrow
                >
                  <WarningIcon />
                </Tooltip>
              )}
            </Box>
          )}
        />
      </FormField>

      <CommitmentDetails
        commitmentDate={selectedCommitment?.commitmentDate}
        capitalCommitmentLcy={selectedCommitment?.capitalCommitmentLcy}
        loanCommitmentLcy={selectedCommitment?.loanCommitmentLcy}
        baseCurrency={baseCurrency}
        totalCommitmentLcy={selectedCommitment?.totalCommitmentLcy}
      />

      <Divider />

      {fields.map((field, index) => (
        <TransfereeField
          key={field.id}
          baseCurrency={baseCurrency}
          totalCommitmentLcy={selectedCommitment?.totalCommitmentLcy}
          index={index}
          availableInvestors={getAvailableInvestorsForTransferee(index)}
          setValue={formMethods.setValue}
          onInvestorChange={handleTransfereeInvestorChange}
          onAdd={addTransferee}
          onRemove={() => removeTransferee(index)}
          onAddNewInvestor={() => openDrawer({ type: 'addInvestor' })}
          isLast={index === fields.length - 1}
          isOnlyTransferee={fields.length === 1}
          control={formMethods.control}
        />
      ))}
      <AddInvestorDrawer
        open={activeDrawer?.type === 'addInvestor'}
        onClose={closeDrawer}
        onSuccess={() => {
          utils.lpInvestor.getAll.invalidate();
          closeDrawer();
        }}
      />
      <DataLossWarningModal
        disclosure={transferrerChangeDisclosure}
        onConfirm={handleConfirmTransferrerChange}
        onCancel={handleCancelTransferrerChange}
        message="Changing the transfer from commitment will reset all transferees. Do you wish to continue?"
      />
    </Page>
  );
};

type TransfereeFieldProps = {
  index: number;
  availableInvestors: Array<{
    id: string;
    investorLabel: string;
  }>;
  onInvestorChange: (index: number, value: string) => void;
  onAdd: () => void;
  onRemove: () => void;
  onAddNewInvestor: () => void;
  setValue: UseFormSetValue<PartnerTransfersFormValues>;
  isLast: boolean;
  isOnlyTransferee: boolean;
  control: Control<PartnerTransfersFormValues>;
  baseCurrency: CurrencyCode;
  totalCommitmentLcy?: number;
};

const TransfereeField: React.FC<TransfereeFieldProps> = ({
  index,
  availableInvestors,
  onInvestorChange,
  onAdd,
  onRemove,
  onAddNewInvestor,
  isLast,
  isOnlyTransferee,
  baseCurrency,
  control,
  totalCommitmentLcy,
  setValue,
}) => {
  const removeDisclosure = useDisclosure();

  const selectedInvestorId = useWatch({
    control,
    name: `transferees.${index}.investorId`,
  });
  const selectedTransfereeLabel = availableInvestors.find(
    (investor) => investor.id === selectedInvestorId,
  )?.investorLabel;

  const handleAmountChange = (value: number) => {
    if (!totalCommitmentLcy) return;
    const newPercentage = (value / totalCommitmentLcy) * 100;
    setValue(`transferees.${index}.percentage`, round(newPercentage, 2));
    //TODO: Reset transaction values
  };

  const handlePercentageChange = (value: number) => {
    if (!totalCommitmentLcy) return;
    const newAmount = (value / 100) * totalCommitmentLcy;
    setValue(`transferees.${index}.amountLcy`, round(newAmount, 2));
    //TODO: Reset transaction values
  };

  return (
    <>
      {index > 0 && <Divider />}
      <FormField
        size="lg"
        label={`Transferee${!isOnlyTransferee ? ` ${index + 1}` : ''}`}
      >
        <Row spacing="xs">
          <Controller
            control={control}
            name={`transferees.${index}.investorId`}
            render={({ field: controllerField }) => (
              <BasicSelect
                fullWidth
                options={availableInvestors.map((investor) => ({
                  label: investor.investorLabel,
                  value: investor.id,
                }))}
                value={controllerField.value}
                onChange={(value) => {
                  controllerField.onChange(value);
                  onInvestorChange(index, value);
                }}
                placeholder="Select an investor"
              />
            )}
          />
          {!isOnlyTransferee && (
            <IconButton
              edge="end"
              onClick={removeDisclosure.onOpen}
              color="error"
              aria-label="Remove transfer"
            >
              <Delete />
            </IconButton>
          )}
        </Row>
      </FormField>
      <FormField size="lg">
        <Row spacing="sm">
          <FormField
            label={`Commitment to transfer${
              selectedTransfereeLabel ? ` to ${selectedTransfereeLabel}` : ''
            }`}
          >
            <Controller
              control={control}
              name={`transferees.${index}.amountLcy`}
              render={({ field: controllerField }) => (
                <NumberInput
                  fullWidth
                  placeholder="Enter amount"
                  currencyStartAdornment
                  currency={baseCurrency}
                  decimals={2}
                  max={totalCommitmentLcy}
                  min={0}
                  value={controllerField.value}
                  onChange={(value) => {
                    controllerField.onChange(value);
                    if (value !== null) {
                      handleAmountChange(value);
                    } else {
                      setValue(`transferees.${index}.percentage`, 0);
                      //TODO: Reset transaction values
                    }
                  }}
                />
              )}
            />
          </FormField>
          <FormField size="xxs" label="Percentage">
            <Controller
              control={control}
              name={`transferees.${index}.percentage`}
              render={({ field: controllerField }) => (
                <NumberInput
                  fullWidth
                  placeholder="Enter percentage"
                  max={100}
                  min={0}
                  decimals={2}
                  showPercentSuffix
                  value={controllerField.value}
                  onChange={(value) => {
                    controllerField.onChange(value);
                    if (value !== null) {
                      handlePercentageChange(value);
                    } else {
                      setValue(`transferees.${index}.amountLcy`, 0);
                      //TODO: Reset transaction values
                    }
                  }}
                />
              )}
            />
          </FormField>
        </Row>
      </FormField>

      {isLast && (
        <>
          <Divider />
          <Row spacing="sm">
            <Button size="large" variant="contained" onClick={onAddNewInvestor}>
              Create new investor
            </Button>
            <Button size="large" variant="outlined" onClick={onAdd}>
              Add additional transferee
            </Button>
          </Row>
        </>
      )}

      <DataLossWarningModal
        message="If you remove this transferee, all information you have input will be lost. Do you wish to continue?"
        disclosure={removeDisclosure}
        onConfirm={onRemove}
        onCancel={removeDisclosure.onClose}
      />
    </>
  );
};

type CommitmentDetailsProps = {
  commitmentDate?: CalendarDay | null;
  totalCommitmentLcy?: number;
  capitalCommitmentLcy?: number;
  loanCommitmentLcy?: number;
  baseCurrency: CurrencyCode;
};

const CommitmentDetails: React.FC<CommitmentDetailsProps> = ({
  commitmentDate,
  capitalCommitmentLcy,
  loanCommitmentLcy,
  baseCurrency,
  totalCommitmentLcy,
}) => {
  return (
    <>
      <FormField labelColorLight size="lg" label="Partner loan commitment">
        <Typography
          variant={typeof loanCommitmentLcy === 'number' ? 'h6' : 'body2'}
        >
          {numberToCurrencyString(loanCommitmentLcy, {
            currency: baseCurrency,
            fallback: '--',
          })}
        </Typography>
      </FormField>
      <FormField labelColorLight size="lg" label="Partner capital contribution">
        <Typography
          variant={typeof capitalCommitmentLcy === 'number' ? 'h6' : 'body2'}
        >
          {numberToCurrencyString(capitalCommitmentLcy, {
            currency: baseCurrency,
            fallback: '--',
          })}
        </Typography>
      </FormField>
      <FormField labelColorLight size="lg" label="Total commitment">
        <Typography
          variant={typeof totalCommitmentLcy === 'number' ? 'h6' : 'body2'}
        >
          {numberToCurrencyString(totalCommitmentLcy, {
            currency: baseCurrency,
            fallback: '--',
          })}
        </Typography>
      </FormField>
      <FormField labelColorLight size="lg" label="Invested on">
        <Typography variant={commitmentDate ? 'h6' : 'body2'}>
          {commitmentDate?.toReadable() ?? '--'}
        </Typography>
      </FormField>
    </>
  );
};

export default AddTransfer;
